'use client'

import { useEffect, useState, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { DEVELOPER_API_NAV } from '@/lib/developer-api-docs'

interface EndpointNavProps {
  className?: string
}

export function EndpointNav({ className }: EndpointNavProps) {
  const [activeId, setActiveId] = useState<string>(DEVELOPER_API_NAV[0].id)

  useEffect(() => {
    const sectionIds = DEVELOPER_API_NAV.map((item) => item.id)
    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[]

    if (elements.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        if (visible[0]?.target.id) {
          setActiveId(visible[0].target.id)
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: [0, 0.25, 0.5, 1] }
    )

    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setActiveId(id)
    }
  }, [])

  return (
    <nav
      aria-label='API documentation sections'
      className={cn(
        'sticky top-28 hidden max-h-[calc(100vh-8rem)] overflow-y-auto lg:block',
        className
      )}
    >
      <p className='mb-3 px-3 text-[10px] font-semibold uppercase tracking-widest text-brand-muted/70'>
        On this page
      </p>
      <ul className='space-y-0.5'>
        {DEVELOPER_API_NAV.map((item) => {
          const isActive = activeId === item.id
          return (
            <li key={item.id}>
              <button
                type='button'
                onClick={() => scrollTo(item.id)}
                aria-current={isActive ? 'true' : undefined}
                className={cn(
                  'w-full rounded-lg px-3 py-2 text-left text-sm transition-all duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower',
                  isActive
                    ? 'bg-brand-cornflower/25 font-medium text-brand-navy ring-1 ring-brand-cornflower/30'
                    : 'text-brand-muted hover:bg-brand-cornflower/10 hover:text-brand-navy'
                )}
              >
                {item.label}
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
